import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, ShieldCheck, User } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const ROLE_LABEL = {
  superadmin: "Superadmin",
  consejo: "Consejo Regulador",
  productor: "Productor",
};

export default function Header() {
  const { user, logout } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const navigate = useNavigate();

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) {
      console.warn("Failed to load notifications:", e);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [user, loadNotifs]);

  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    loadNotifs();
  };

  const markAll = async () => {
    await api.put("/notifications/read-all");
    loadNotifs();
  };

  const homePath = user?.role === "superadmin" ? "/admin" : user?.role === "consejo" ? "/consejo" : "/productor";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-stone-200" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to={homePath} className="flex items-center gap-3" data-testid="header-home-link">
          <img src="/logo-pimenton.png" alt="DOP Pimentón de la Vera" className="h-11 w-11 object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-stone-900 text-base">DOP Pimentón de la Vera</div>
            <div className="text-xs text-stone-500">Gestión Documental Oficial</div>
          </div>
        </Link>

        {user && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="btn-notifications">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel>Notificaciones</DropdownMenuLabel>
                  {unread > 0 && (
                    <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="btn-mark-all-read">
                      Marcar todas
                    </button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notifs.length === 0 && (
                  <div className="px-3 py-6 text-sm text-stone-500 text-center">Sin notificaciones</div>
                )}
                <div className="max-h-80 overflow-y-auto">
                  {notifs.slice(0, 12).map((n) => (
                    <DropdownMenuItem key={n.id} onClick={() => markRead(n.id)} className="flex items-start gap-2 py-2.5">
                      <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-stone-300" : "bg-primary"}`} />
                      <div className="flex-1">
                        <div className="text-sm text-stone-800">{n.message}</div>
                        <div className="text-[11px] text-stone-500 mt-0.5">{new Date(n.created_at).toLocaleString("es-ES")}</div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2 pr-3" data-testid="btn-user-menu">
                  <div className="h-8 w-8 rounded-full bg-stone-900 text-white flex items-center justify-center font-semibold text-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="hidden md:block text-left leading-tight">
                    <div className="text-sm font-medium text-stone-900">{user.name}</div>
                    <div className="text-[11px] text-stone-500">{ROLE_LABEL[user.role]}</div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-stone-500 font-normal">{user.email}</div>
                  <Badge className="mt-2 bg-paprika-100 text-paprika-600 border-paprika-200" data-testid="badge-user-role">
                    {ROLE_LABEL[user.role]}
                  </Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(homePath)}>
                  <User className="h-4 w-4 mr-2" /> Mi panel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="btn-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </header>
  );
}
